import type { ShopperBasketsTypes, ShopperOrdersTypes } from 'commerce-sdk-isomorphic'
import { middlewareGateway } from '~/gateway/middleware.gateway'

export type updateOrderPaymentInstrumentOpts = {
  orderNo: ShopperOrdersTypes.Order['orderNo']
  amount: number
  paymentInstrument: ShopperOrdersTypes.OrderPaymentInstrument
  transactionData: object
}

export default function useOrder() {
  const nuxtApp = useNuxtApp()
  const locale = nuxtApp.$i18n.locale.value === 'fr' ? localeConfig[nuxtApp.$i18n.locale.value].code : ''
  // SFCC expects the parameter omitted outside French; an empty string is not a valid locale.
  const localeParams: { locale?: string } = locale ? { locale } : {}

  async function getOrder(orderNo: ShopperOrdersTypes.Order['orderNo']) {
    const { getAuthHeaders, shopperOrder } = useShopper()

    if (!orderNo) {
      throw new Error('Order number is required')
    }

    try {
      return await shopperOrder.getOrder({
        headers: await getAuthHeaders(),
        parameters: {
          ...localeParams,
          orderNo: orderNo as string,
        },
      })
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }
  async function getOrderWithProductData(orderNo: string) {
    try {
      const order = await getOrder(orderNo)
      if (!order || order instanceof Error) {
        throw new Error('Order not found')
      }

      const updatedOrder: OrderWithProductData = { ...order }
      if (order?.productItems?.length) {
        const productItemsWithData = await mergeProductItemsWithProductData(order.productItems)
        updatedOrder.productItems = productItemsWithData
      }

      return updatedOrder
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function createOrder(basketId: ShopperBasketsTypes.Basket['basketId']) {
    const { getAuthHeaders, shopperOrder } = useShopper()
    const { getRecaptchaToken, validateRecaptchaToken } = useRecaptcha()
    try {
      const recaptcha = await getRecaptchaToken()
      const recaptchaResponse = await validateRecaptchaToken(recaptcha as string)

      if (!recaptchaResponse) {
        throw new Error('Recaptcha validation failed')
      }

      return await shopperOrder.createOrder({
        headers: await getAuthHeaders(),
        body: {
          basketId,
        },
        parameters: {
          ...localeParams,
        },
      })
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function updateOrderPaymentInstrument(options: updateOrderPaymentInstrumentOpts) {
    const { getAuthHeaders, shopperOrder } = useShopper()
    const { orderNo, amount, paymentInstrument, transactionData } = options

    if (!orderNo) {
      throw new Error('Order number is required')
    }

    try {
      return await shopperOrder.updatePaymentInstrumentForOrder({
        headers: await getAuthHeaders(),
        body: {
          amount,
          paymentMethodId: paymentInstrument.paymentMethodId,
          c_transactionData: JSON.stringify(transactionData),
        },
        parameters: {
          ...localeParams,
          orderNo,
          paymentInstrumentId: paymentInstrument.paymentInstrumentId || '',
        },
      })
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function failOrder(orderNo: ShopperOrdersTypes.Order['orderNo']): Promise<boolean> {
    // Exponential backoff due to critical endpoint: initial 300ms, doubles each retry, max 3 retries (4 total attempts)
    const maxRetries = 3
    let attempt = 0
    let delay = 300 // ms

    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

    while (attempt <= maxRetries) {
      try {
        await middlewareGateway(`/next-gen/sfcc/order/fail-order?orderNo=${orderNo}`, {
          method: 'PUT',
        })
        await useBasketsStore().init() // Refresh basket after failing order
        return true
      } catch (e) {
        attempt += 1
        console.error(`useOrder/failOrder attempt ${attempt} failed`, e)

        if (attempt > maxRetries) {
          break
        }

        // Add a small jitter (0-100ms) to reduce synchronized retries
        const jitter = Math.floor(Math.random() * 100)
        await sleep(delay + jitter)
        delay = delay * 2
      }
    }

    return false
  }

  return {
    getOrder,
    getOrderWithProductData,
    createOrder,
    updateOrderPaymentInstrument,
    failOrder,
  }
}
